import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, ArrowLeft, FileText, Boxes, ListChecks, Package, Cpu, Plus } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import MetaCard from "@/components/MetaCard";
import StatusBadge from "@/components/StatusBadge";
import FileUploader from "@/components/FileUploader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/format";
import { readBack, useWithBack, segmentBackOverride } from "@/lib/nav";

const KIND_LABEL = {
  yes_no: "Yes/No",
  ok_issue: "OK/Issue",
  numeric: "Numeric",
  text: "Text",
};

export default function MachineTypeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [searchParams] = useSearchParams();
  const backCtx = readBack(searchParams);
  const withBackHref = useWithBack();
  const [type, setType] = useState(null);
  const [machinesOfType, setMachinesOfType] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: t }, { data: m }] = await Promise.all([
        api.get(`/machine-types/${id}`),
        api.get(`/machines`, { params: { machine_type_id: id, limit: 500 } }),
      ]);
      setType(t);
      setMachinesOfType(m.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/machine-types", { replace: true });
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading || !type) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="p-6 space-y-6" data-testid="machine-type-detail-page">
      <Breadcrumbs
        items={[
          { label: "Settings" },
          { label: "Machine Types", to: segmentBackOverride("/machine-types", backCtx?.to) },
          { label: type.name },
        ]}
      />
      <PageHeader
        testId="machine-type-detail-header"
        title={type.name}
        subtitle={
          <span className="flex items-center gap-2 flex-wrap">
            {type.active ? <StatusBadge tone="success">Active</StatusBadge> : <StatusBadge tone="muted">Inactive</StatusBadge>}
            <span>{type.machine_count} machines · {type.default_checklist?.length ?? 0} checklist items</span>
          </span>
        }
        actions={<>
          <Button asChild variant="outline" size="sm" data-testid="machine-type-detail-back"><Link to={backCtx?.to || "/machine-types"}><ArrowLeft className="h-4 w-4" /> Back</Link></Button>
          {hasPermission("machine_type.write") && (<Button asChild size="sm" data-testid="machine-type-detail-edit"><Link to={`/machine-types/${type.id}/edit`}><Pencil className="h-4 w-4" /> Edit</Link></Button>)}
        </>}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {type.description && (
            <Card className="rounded-md">
              <CardHeader><CardTitle className="text-sm font-medium flex items-center gap-2"><Boxes className="h-4 w-4 text-primary" /> Description</CardTitle></CardHeader>
              <CardContent className="text-sm leading-relaxed whitespace-pre-wrap">{type.description}</CardContent>
            </Card>
          )}

          <Card className="rounded-md">
            <CardHeader><CardTitle className="text-sm font-medium flex items-center gap-2"><ListChecks className="h-4 w-4 text-primary" /> Default checklist</CardTitle></CardHeader>
            <CardContent className="p-0">
              {(type.default_checklist || []).length === 0 ? (
                <div className="py-8 text-center text-xs text-muted-foreground">No checklist items.</div>
              ) : (
                <ol className="divide-y divide-border">
                  {type.default_checklist.map((it, idx) => (
                    <li key={it.id} className="flex items-start justify-between gap-3 p-3">
                      <div>
                        <div className="text-sm font-medium">{idx + 1}. {it.label}</div>
                        <div className="text-xs text-muted-foreground uppercase tracking-wider">
                          {KIND_LABEL[it.kind] || it.kind}{it.required ? " · required" : ""}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-md">
            <CardHeader><CardTitle className="text-sm font-medium flex items-center gap-2"><Package className="h-4 w-4 text-primary" /> Default parts picklist</CardTitle></CardHeader>
            <CardContent className="p-0">
              {(type.default_parts_picklist || []).length === 0 ? (
                <div className="py-8 text-center text-xs text-muted-foreground">No default parts.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      <th className="text-left py-2 px-3">SKU</th>
                      <th className="text-left py-2 px-3">Name</th>
                      <th className="text-left py-2 px-3">Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {type.default_parts_picklist.map((p) => (
                      <tr key={p.id} className="border-b border-border last:border-0">
                        <td className="py-2 px-3 font-mono text-xs">{p.sku}</td>
                        <td className="py-2 px-3">{p.name}</td>
                        <td className="py-2 px-3 text-muted-foreground">{p.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-md">
            <CardHeader><CardTitle className="text-sm font-medium flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> Manuals</CardTitle></CardHeader>
            <CardContent>
              <FileUploader files={type.manuals || []} readOnly testIdPrefix="machine-type-detail-manuals" emptyLabel="No manuals attached." />
            </CardContent>
          </Card>

          <Card className="rounded-md" data-testid="machine-type-machines-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Cpu className="h-4 w-4 text-primary" /> Machines of this type ({machinesOfType.length})
              </CardTitle>
              {hasPermission("machine.write") && (
                <Button asChild size="sm" variant="outline" data-testid="machine-type-add-machine-button">
                  <Link to={`/machines/new?machine_type_id=${type.id}`}><Plus className="h-3.5 w-3.5" /> Add machine</Link>
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {machinesOfType.length === 0 ? (
                <div className="py-8 text-center text-xs text-muted-foreground" data-testid="machine-type-machines-empty">
                  No machines of this type yet.
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {machinesOfType.map((m) => (
                    <li key={m.id} className="p-3 flex items-center justify-between hover:bg-muted/30" data-testid={`machine-type-machine-${m.id}`}>
                      <div>
                        <Link to={withBackHref(`/machines/${m.id}`)} className="text-sm font-medium hover:text-primary" data-testid={`machine-type-machine-link-${m.id}`}>
                          {m.make} {m.model}
                        </Link>
                        <div className="text-xs text-muted-foreground font-mono">Serial {m.serial || "—"}</div>
                      </div>
                      {m.active ? <StatusBadge tone="success">Active</StatusBadge> : <StatusBadge tone="muted">Inactive</StatusBadge>}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <MetaCard
            title="Metadata"
            fields={[
              { label: "Status", value: type.active ? "Active" : "Inactive" },
              { label: "Machines", value: type.machine_count, testId: "machine-type-meta-machine-count" },
              { label: "Checklist", value: type.default_checklist?.length ?? 0 },
              { label: "Parts", value: type.default_parts_picklist?.length ?? 0 },
              { label: "Manuals", value: type.manuals?.length ?? 0 },
              { label: "Created", value: formatDate(type.created_at) },
              { label: "Updated", value: formatDate(type.updated_at) },
            ]}
          />
        </div>
      </div>
    </div>
  );
}
